import { useEffect, useState } from "react";
import { Drawer, Button, Tabs } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import { getKPIStatisticsThunk } from "../../../../Thunks";
import styles from "../statistics.module.css";
import { formatDateWithDots } from "..";

const KPI_LABELS = {
  projectMaturityParameter: "Maturity",
  lawsuitsLevel: "Lawsuits",
  smartContractPlatformSecurityAuditLevel: "Project Risk",
  projectSecurityLevel: "Historical Risk",
  amlLevel: "AML",
};
const highRiskValues = [
  { key: "high", value: "High", color: "#FF4D4F" },
  { key: "immature", value: "Immature", color: "#FF4D4F" },
  { key: "unreliable", value: "Unreliable", color: "#FF4D4F" },
  { key: "negative", value: "Negative", color: "#FF4D4F" },
  { key: "non-compliant", value: "Non-Compliant", color: "#FF4D4F" },
  { key: "limited", value: "Limited", color: "#FF4D4F" },
  { key: "centralized", value: "Centralized", color: "#FF4D4F" },
  { key: "lawsuits exist", value: "Lawsuits Exist", color: "#FF4D4F" },
  { key: "aml activities", value: "AML Activities", color: "#FF4D4F" },
];

const mediumRiskValues = [
  { key: "medium", value: "Medium", color: "#FAAD14" },
  { key: "anonymous", value: "Anonymous", color: "#FAAD14" },
  { key: "standard", value: "Standard", color: "#FAAD14" },
  { key: "not fi", value: "Not FI", color: "#FAAD14" },
];

const lowRiskValues = [
  { key: "low", value: "Low", color: "#4DC7B5" },
  { key: "mature", value: "Mature", color: "#4DC7B5" },
  { key: "reliable", value: "Reliable", color: "#4DC7B5" },
  { key: "positive", value: "Positive", color: "#4DC7B5" },
  { key: "no lawsuits", value: "No Lawsuits", color: "#4DC7B5" },
  { key: "none", value: "None", color: "#4DC7B5" },
  { key: "compliant", value: "Compliant", color: "#4DC7B5" },
  { key: "extensive", value: "Extensive", color: "#4DC7B5" },
  { key: "decentralized", value: "Decentralized", color: "#4DC7B5" },
  { key: "no aml activities", value: "No AML Activities", color: "#4DC7B5" },
];

const getValueColor = (value) => {
  if (!value) return [];

  const parts = value
    .split(" - ")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

  return parts.map((part) => {
    const lower = part.toLowerCase();

    // Search in each group
    let item =
      highRiskValues.find((i) => i.key === lower) ||
      mediumRiskValues.find((i) => i.key === lower) ||
      lowRiskValues.find((i) => i.key === lower);

    if (!item) item = { value: part, color: "#8C8C8C" };
    return { value: item.value, color: item.color };
  });
};

export const KPIDrawer = ({ isOpen, onClose, selectedChange }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("all");

  const assetsUserKPIStatistics = useSelector((state) => {
    return state.ListingReducer.assetsUserKPIStatistics;
  });
  useEffect(() => {
    if (isOpen && selectedChange?.id) {
      dispatch(
        getKPIStatisticsThunk({
          id: selectedChange.id,
          trendFields: activeTab === "all" ? "" : activeTab,
        }),
      );
    }
  }, [isOpen, selectedChange?.id, activeTab, dispatch]);

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const filteredData =
    activeTab === "all"
      ? Array.isArray(assetsUserKPIStatistics)
        ? assetsUserKPIStatistics
        : []
      : Array.isArray(assetsUserKPIStatistics)
        ? assetsUserKPIStatistics.filter((item) => item.name === activeTab)
        : [];

  const renderContent = () => {
    if (!filteredData?.length) return <p>No KPI data found.</p>;

    return (
      <div className={styles.kpiList}>
        {filteredData.map((item, index) => {
          const coloredValues = getValueColor(item.change);
          return (
            <div
              key={index}
              className={styles.kpiItem}
              style={{
                borderBottom: "1px solid #CBCBD8",
                padding: "15px 3px",
                marginBottom: "5px",
              }}
            >
              <p style={{ margin: 0, color: "#000" }}>
                {formatDateWithDots(item.createdAt)}
              </p>
              <div
                style={{
                  display: "flex",
                  width: "200px",
                  alignItems: "baseline",
                  justifyContent: "flex-end",
                }}
              >
                <p
                  style={{
                    margin: "3px 0",
                    display: "flex",
                    alignItems: "end",
                    justifyContent: "flex-end",
                    whiteSpace: "nowrap",
                  }}
                >
                  {KPI_LABELS[item.name] || item.name}
                </p>
                {item.value === "higher" && (
                  <span
                    className={styles.greenTriangle}
                    style={{ cursor: "pointer" }}
                  ></span>
                )}
                {item.value === "lower" && (
                  <span
                    className={styles.redTriangle}
                    style={{ cursor: "pointer" }}
                  ></span>
                )}
                {item.value === "same" && (
                  <span
                    className={styles.blueSquare}
                    style={{ cursor: "pointer" }}
                  ></span>
                )}
              </div>
              <p
                style={{
                  margin: "3px 0",
                  width: "210px",
                  display: "flex",
                  justifyContent: "right",
                }}
              >
                <div style={{ display: "flex", gap: "6px" }}>
                  {coloredValues.map((item, idx) => (
                    <span
                      key={idx}
                      style={{
                        borderRadius: "4px",
                        color: item.color,
                        fontSize: "12px",
                      }}
                    >
                      {item.value}
                      {idx < coloredValues.length - 1 ? " -" : ""}
                    </span>
                  ))}
                </div>
              </p>
            </div>
          );
        })}
      </div>
    );
  };
  const tabLabelStyle = {
    fontFamily: "Assistant, sans-serif",
    fontWeight: 600,
    fontSize: "18px",
    color: "#889096",
  };
  return (
    <Drawer
      title={
        <div
          style={{
            fontWeight: 700,
            fontSize: "22px",
            color: "#1F1F1F",
            fontFamily: "Assistant",
          }}
        >
          KPI Changes in the last 180 days
        </div>
      }
      placement="right"
      open={isOpen}
      width={600}
      closeIcon={false}
      bodyStyle={{ padding: "0px 24px 24px 24px" }}
      style={{
        backgroundColor: "#f0f0f3",
      }}
      extra={
        <Button
          type="text"
          icon={<CloseOutlined style={{ fontSize: 16, color: "#999" }} />}
          onClick={onClose}
          style={{
            position: "absolute",
            right: 16,
            top: 16,
          }}
        />
      }
    >
      <div className={styles.subtitle}>
        Asset :{" "}
        <strong style={{ paddingLeft: "5px" }}> {selectedChange?.asset}</strong>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        tabBarGutter={20}
        items={[
          { key: "all", label: <span style={tabLabelStyle}>All</span> },
          {
            key: "projectMaturityParameter",
            label: <span style={tabLabelStyle}>Maturity</span>,
          },
          {
            key: "smartContractPlatformSecurityAuditLevel",
            label: <span style={tabLabelStyle}>Project Risk</span>,
          },
          {
            key: "projectSecurityLevel",
            label: <span style={tabLabelStyle}>Historical Risk</span>,
          },
          { key: "amlLevel", label: <span style={tabLabelStyle}>AML</span> },
          {
            key: "lawsuitsLevel",
            label: <span style={tabLabelStyle}>Lawsuits</span>,
          },
        ]}
      />

      <div style={{ marginTop: 10 }}>{renderContent()}</div>
    </Drawer>
  );
};
